if (!customElements.get('dcs-subscription-price')) {
  customElements.define(
    'dcs-subscription-price',
    class DcsSubscriptionPrice extends HTMLElement {
      connectedCallback() {
        const priceData = this.querySelector('[data-dcs-subscription-prices]');

        if (!priceData) return;

        try {
          this.planPrices = JSON.parse(priceData.textContent);
        } catch {
          return;
        }

        // 無料トライアルは初回だけ（調整要望134行目）。隠すプランと、隠したプランの代わりに選ぶプラン。
        // 初回から有料のプランが商品に無いときはテーマが出力しないので null のまま（従来どおり）。
        this.planFilter = this.readPlanFilter();

        this.productInfo = this.closest('product-info');
        this.handlePlanChange = this.handlePlanChange.bind(this);
        this.productInfo?.addEventListener('change', this.handlePlanChange);

        // プラン紹介ページから ?selling_plan=<ID> 付きで来たときに、そのプランを初期選択する。
        // GO SUB のラジオは Shopify 標準の selling_plan パラメータを見ないため、ここで補う。
        // セレクタに入れる値なので数字以外は捨てる。
        const requestedPlan = new URLSearchParams(window.location.search).get('selling_plan');
        this.requestedPlanId = /^\d+$/.test(requestedPlan || '') ? requestedPlan : null;

        // GO SUB でプランを作り直すとIDが変わり、テーマ設定の値が古くなる。そのまま黙って
        // 先頭プラン（月額）で開くと、年額の導線から月額を契約させてしまう不具合が再発するので、
        // この商品の販売プランに無いIDは適用せず警告を残す。購入自体は従来どおり通す。
        if (this.requestedPlanId && !this.planPrices[this.requestedPlanId]) {
          console.warn(
            `[dcs-subscription-price] selling_plan=${this.requestedPlanId} はこの商品の販売プランに存在しません。` +
              'テーマ設定「販売プランID（GO SUB）」を確認してください。'
          );
          this.requestedPlanId = null;
        }

        // プラン紹介ページのリンクは無料トライアルのプランIDを持っているので、無料トライアルを使ったことが
        // ある会員では、定期価格が同じ初回から有料のプランへ読み替える（月額→月額、年額→年額）。
        if (this.requestedPlanId && this.isHiddenPlan(this.requestedPlanId)) {
          this.requestedPlanId = this.replacementPlanId(this.requestedPlanId);
        }

        this.observer = new MutationObserver(() => {
          this.applyRequestedPlan();
          this.enforcePlanFilter();
          this.syncPrice();
        });
        this.observer.observe(this.productInfo || this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['checked'],
        });

        this.applyRequestedPlan();
        this.enforcePlanFilter();
        this.syncPrice();
      }

      readPlanFilter() {
        const filterData = this.querySelector('[data-dcs-plan-filter]');
        if (!filterData) return null;

        try {
          const filter = JSON.parse(filterData.textContent);
          if (!Array.isArray(filter.hidden) || filter.hidden.length === 0) return null;
          return {
            hidden: new Set(filter.hidden.map(String)),
            map: filter.map || {},
            fallback: filter.fallback ? String(filter.fallback) : null,
          };
        } catch {
          return null;
        }
      }

      isHiddenPlan(planId) {
        return Boolean(this.planFilter && this.planFilter.hidden.has(String(planId)));
      }

      replacementPlanId(planId) {
        if (!this.planFilter) return planId;
        const mapped = this.planFilter.map[String(planId)] || this.planFilter.fallback;
        return mapped && this.planPrices[mapped] ? String(mapped) : null;
      }

      // GO SUB のウィジェットは非同期で描画されるので、ラジオが現れるまで MutationObserver から
      // 繰り返し呼ばれる。適用は一度だけでよいのでフラグで止める。
      applyRequestedPlan() {
        if (!this.requestedPlanId || this.requestedPlanApplied) return;

        const input = this.productInfo?.querySelector(
          `[data-gosub-selling-plan-input][value="${this.requestedPlanId}"]`
        );

        if (!input) return;

        this.requestedPlanApplied = true;

        // checked を直接書き換えると GO SUB 側のハンドラが走らないので click する。
        if (!input.checked) input.click();
      }

      // 隠したプラン（CSS で非表示）が選ばれていたら、代わりのプランを選び直す。
      // GO SUB は先頭のプラン（無料トライアルの月額）を初期選択するため、無料トライアルを使ったことが
      // ある会員では必ずここを通る。選び直した後の変化でもう一度呼ばれるが、そのときは何もしない。
      enforcePlanFilter() {
        if (!this.planFilter) return;

        const checked = this.productInfo?.querySelector('[data-gosub-selling-plan-input]:checked');
        if (!checked || !this.isHiddenPlan(checked.value)) return;

        const replacementId = this.replacementPlanId(checked.value);
        if (!replacementId) return;

        const replacement = this.productInfo?.querySelector(
          `[data-gosub-selling-plan-input][value="${replacementId}"]`
        );
        if (replacement && !replacement.checked) replacement.click();
      }

      disconnectedCallback() {
        this.productInfo?.removeEventListener('change', this.handlePlanChange);
        this.observer?.disconnect();
      }

      handlePlanChange(event) {
        if (event.target.matches('[data-gosub-selling-plan-input]')) {
          this.syncPrice(event.target.value);
        }
      }

      syncPrice(planId) {
        const selectedPlanId = planId || this.getSelectedPlanId();
        const recurringPrice = this.planPrices[selectedPlanId];

        if (!recurringPrice) return;

        this.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--last').forEach((price) => {
          if (price.textContent.trim() !== recurringPrice) {
            price.textContent = recurringPrice;
          }
        });
      }

      getSelectedPlanId() {
        const checkedPlan = this.productInfo?.querySelector('[data-gosub-selling-plan-input]:checked');
        if (checkedPlan?.value) return checkedPlan.value;

        // ウィジェットの描画前は、隠していないプランのうち先頭の価格を出す
        const visiblePlanId = Object.keys(this.planPrices).find((id) => !this.isHiddenPlan(id));
        return visiblePlanId || Object.keys(this.planPrices)[0];
      }
    }
  );
}
